import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/auth/AuthProvider';
import PurchaseStatusChecker from '../components/PurchaseStatusChecker';
import RealtimeStatusIndicator from '../components/RealtimeStatusIndicator';
import DataExporter from '../components/DataExporter';

const DatabaseTest = () => {
  const { user } = useAuth();
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'failed'>('unknown');
  const [autoExportTriggered, setAutoExportTriggered] = useState(false);

  const runTest = async (testName: string, testFn: () => Promise<any>) => {
    const startTime = Date.now();
    try {
      const result = await testFn();
      const duration = Date.now() - startTime;
      
      setTestResults(prev => [...prev, {
        name: testName,
        status: 'success',
        result,
        duration,
        timestamp: new Date().toISOString()
      }]);
      
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      setTestResults(prev => [...prev, {
        name: testName,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration,
        timestamp: new Date().toISOString()
      }]);
      
      throw error;
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    setConnectionStatus('unknown');

    try {
      // Test 1: Basic connection
      await runTest('Database Connection', async () => {
        const { data, error } = await supabase
          .from('stripe_customers')
          .select('id')
          .limit(1);
        
        if (error) throw error;
        setConnectionStatus('connected');
        return { message: 'Connection successful', recordCount: data?.length || 0 };
      });

      // Test 2: Check stripe_customers table
      await runTest('Stripe Customers Table', async () => {
        const { data, error } = await supabase
          .from('stripe_customers')
          .select('stripe_customer_id, email, user_id, created_at')
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) throw error;
        return { 
          message: `Found ${data?.length || 0} customer records`,
          customers: data?.map(c => ({
            customerId: c.stripe_customer_id,
            email: c.email,
            userId: c.user_id,
            createdAt: c.created_at
          })) || []
        };
      });

      // Test 3: Check stripe_subscriptions table
      await runTest('Stripe Subscriptions Table', async () => {
        const { data, error } = await supabase
          .from('stripe_subscriptions')
          .select('stripe_subscription_id, stripe_customer_id, status, price_id, created_at')
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) throw error;
        return { 
          message: `Found ${data?.length || 0} subscription records`,
          subscriptions: data?.map(s => ({
            subscriptionId: s.stripe_subscription_id,
            customerId: s.stripe_customer_id,
            status: s.status,
            priceId: s.price_id,
            createdAt: s.created_at
          })) || []
        };
      });

      // Test 4: Check stripe_payments table
      await runTest('Stripe Payments Table', async () => {
        const { data, error } = await supabase
          .from('stripe_payments')
          .select('stripe_payment_intent_id, user_id, amount, currency, status, created_at')
          .eq('status', 'succeeded')
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) throw error;
        return { 
          message: `Found ${data?.length || 0} successful payment records`,
          payments: data?.map(p => ({
            paymentId: p.stripe_payment_intent_id,
            userId: p.user_id,
            amount: p.amount,
            currency: p.currency,
            createdAt: p.created_at
          })) || []
        };
      });

      // Test 5: Check current user's records
      if (user) {
        await runTest('Current User Records', async () => {
          // Check customer record
          const { data: customerData, error: customerError } = await supabase
            .from('stripe_customers')
            .select('*')
            .ilike('email', user.email!)
            .maybeSingle();

          // Check subscriptions
          const { data: subscriptionData, error: subError } = await supabase
            .from('stripe_subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          // Check payments
          const { data: paymentData, error: payError } = await supabase
            .from('stripe_payments')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'succeeded')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return {
            userEmail: user.email,
            userId: user.id,
            customerRecord: customerData,
            subscriptionRecord: subscriptionData,
            paymentRecord: paymentData,
            hasAnyRecord: !!(customerData || subscriptionData || paymentData)
          };
        });
      }

      // Test 6: Test the view
      await runTest('Stripe User Subscriptions View', async () => {
        const { data, error } = await supabase
          .from('stripe_user_subscriptions')
          .select('*')
          .limit(10);
        
        if (error) throw error;
        return { 
          message: `View returned ${data?.length || 0} records`,
          viewData: data || []
        };
      });

    } catch (error) {
      console.error('Test suite failed:', error);
      setConnectionStatus('failed');
    } finally {
      setIsRunning(false);
    }
  };

  // Auto-trigger CSV export when component mounts
  const triggerAutoExport = async () => {
    if (!user || autoExportTriggered) return;
    
    setAutoExportTriggered(true);
    
    try {
      console.log('Auto-triggering CSV export for user:', user.email);
      
      // Get user profiles data
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user profiles:', error);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No user profile data found to export');
        return;
      }

      // Format data for CSV
      const formattedData = data.map(profile => ({
        user_id: profile.user_id || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        country: profile.country || '',
        created_at: profile.created_at || '',
        updated_at: profile.updated_at || ''
      }));

      // Create CSV content
      const headers = Object.keys(formattedData[0]);
      const csvRows = [
        headers.join(','), // Header row
        ...formattedData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ];
      
      const csvContent = csvRows.join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `user_profiles_export_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      console.log(`✅ CSV export completed: ${filename} (${data.length} records)`);
      
    } catch (error) {
      console.error('Auto-export error:', error);
    }
  };

  useEffect(() => {
    // Auto-run tests on component mount
    runAllTests();
    // Auto-trigger CSV export
    triggerAutoExport();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 pt-16">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="w-8 h-8 text-teal-600" />
            <h1 className="text-3xl font-bold text-teal-900">Database Connection Test</h1>
          </div>
          
          <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg border ${getConnectionStatusColor()}`}>
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' :
              connectionStatus === 'failed' ? 'bg-red-500' : 'bg-gray-500'
            }`}></div>
            <span className="font-medium">
              {connectionStatus === 'connected' ? 'Database Connected' :
               connectionStatus === 'failed' ? 'Database Connection Failed' : 'Connection Status Unknown'}
            </span>
          </div>
          
          <RealtimeStatusIndicator
            isConnected={false}
            hasActiveSubscription={false}
            isPreOrder={false}
            loading={false}
            error="Real-time features disabled"
            className="ml-4"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Test Results */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Test Results</h2>
              <button
                onClick={runAllTests}
                disabled={isRunning}
                className="flex items-center space-x-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Running...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Run Tests</span>
                  </>
                )}
              </button>
            </div>

            <div className="space-y-4">
              {testResults.map((test, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(test.status)}
                      <span className="font-medium text-gray-900">{test.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{test.duration}ms</span>
                  </div>
                  
                  {test.status === 'success' ? (
                    <div className="text-sm text-gray-600">
                      <p className="mb-2">{test.result.message}</p>
                      {test.result.customers && test.result.customers.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-700">
                            View {test.result.customers.length} customer records
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                            {JSON.stringify(test.result.customers, null, 2)}
                          </pre>
                        </details>
                      )}
                      {test.result.subscriptions && test.result.subscriptions.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-700">
                            View {test.result.subscriptions.length} subscription records
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                            {JSON.stringify(test.result.subscriptions, null, 2)}
                          </pre>
                        </details>
                      )}
                      {test.result.payments && test.result.payments.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-700">
                            View {test.result.payments.length} payment records
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                            {JSON.stringify(test.result.payments, null, 2)}
                          </pre>
                        </details>
                      )}
                      {test.result.hasAnyRecord !== undefined && (
                        <div className="mt-2 p-2 bg-blue-50 rounded">
                          <p className="text-blue-800 text-sm">
                            <strong>User has purchase records:</strong> {test.result.hasAnyRecord ? 'Yes' : 'No'}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-red-600 text-sm">{test.error}</p>
                  )}
                </div>
              ))}
              
              {testResults.length === 0 && !isRunning && (
                <>
                  <div>Real-time Connected: ❌ No (Disabled)</div>
                  <div>Has Active Subscription: ❌ No (Disabled)</div>
                  <div>Is Pre-Order: ❌ No (Disabled)</div>
                  <p className="text-gray-500 text-center py-8">No tests run yet. Click "Run Tests" to start.</p>
                </>
              )}
            </div>
          </div>

          {/* Purchase Status Checker */}
          <PurchaseStatusChecker />
        </div>

        {/* Data Export Section */}
        <div className="mt-8">
          <DataExporter />
        </div>

        {/* Environment Info */}
        <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Environment Information</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Supabase Configuration</h3>
              <div className="space-y-1 text-gray-600">
                <div>URL: {import.meta.env.VITE_SUPABASE_URL ? '✅ Configured' : '❌ Missing'}</div>
                <div>Anon Key: {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Configured' : '❌ Missing'}</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">User Information</h3>
              <div className="space-y-1 text-gray-600">
                <div>Logged In: {user ? '✅ Yes' : '❌ No'}</div>
                {user && (
                  <>
                    <div>Email: {user.email}</div>
                    <div>User ID: {user.id}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseTest;